import './Home.scss';
import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowRight } from 'lucide-react';
import useNameFX from './useNameFX';

const NAME = 'Danilo Braga';

// Frases do "digitador" — edite à vontade (mantenha o array fora do componente)
const ROLES = [
  'Desenvolvedor Web',
  'Full-Stack',
  'React · Node.js · PostgreSQL',
];

const SR_ROLE = 'Desenvolvedor web full-stack: React, Node.js e PostgreSQL';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function useTypewriter(words) {
  const [text, setText] = useState(() => (prefersReducedMotion() ? words[0] : ''));

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    let w = 0;
    let i = 0;
    let deleting = false;
    let timer;

    const tick = () => {
      const word = words[w];

      if (!deleting) {
        i += 1;
        setText(word.slice(0, i));
        if (i === word.length) {
          deleting = true;
          timer = setTimeout(tick, 1700); // pausa com a frase completa
          return;
        }
        timer = setTimeout(tick, 70);
      } else {
        i -= 1;
        setText(word.slice(0, i));
        if (i === 0) {
          deleting = false;
          w = (w + 1) % words.length;
          timer = setTimeout(tick, 350);
          return;
        }
        timer = setTimeout(tick, 35);
      }
    };

    timer = setTimeout(tick, 700);
    return () => clearTimeout(timer);
  }, [words]);

  return text;
}

function Home({ scrollToSection, sobreRef, projetosRef }) {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const bgRef = useRef(null);
  const nameRef = useRef(null);
  const fxRef = useRef(null);
  const canvasRef = useRef(null);

  const role = useTypewriter(ROLES);

  // Física das letras, partículas, combo, chacoalhar, inclinação e scroll elástico
  useNameFX({ rootRef, stageRef, bgRef, nameRef, fxRef, canvasRef });

  return (
    <section id="home" className="home" ref={rootRef}>

      {/* Fundo decorativo */}
      <div className="home-bg" ref={bgRef} aria-hidden="true">
        <span className="home-aurora a" />
        <span className="home-aurora b" />
        <span className="home-grid" />
        <span className="home-glow" />
      </div>

      {/* Palco: recebe parallax / inclinação */}
      <div className="home-stage" ref={stageRef}>

        {/* Nome + cargo */}
        <div className="home-logo">
          <h1 className="home-title">
            <span className="home-name" ref={nameRef}>
              <span className="sr-only">{NAME}</span>
              <span aria-hidden="true">
                {[...NAME].map((ch, i) =>
                  ch === ' ' ? (
                    <span key={i} className="space">{'\u00A0'}</span>
                  ) : (
                    <span key={i} className="letter" data-letter>{ch}</span>
                  )
                )}
              </span>
            </span>
            <span className="home-role">
              <span className="sr-only">{SR_ROLE}</span>
              <span className="typed" aria-hidden="true">{role}</span>
            </span>
          </h1>
        </div>

        {/* CTAs */}
        <div className="home-cta">
          <button
            type="button"
            className="cta-primary"
            onClick={() => scrollToSection(projetosRef)}
          >
            Ver projetos
            <ArrowRight size={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cta-secondary"
            onClick={() => scrollToSection(sobreRef)}
          >
            Sobre mim
          </button>
        </div>

        {/* Dica de interação */}
        <p className="home-tip" aria-hidden="true">
          <span className="tip-desktop">passe o mouse · clique no meu nome</span>
          <span className="tip-touch">toque no meu nome · chacoalhe o celular</span>
        </p>
      </div>

      {/* Camada de efeitos (partículas + textos de combo) */}
      <div className="home-fx" ref={fxRef} aria-hidden="true">
        <canvas ref={canvasRef} className="home-fx-canvas" />
      </div>

      {/* Scroll hint */}
      <button
        type="button"
        className="scroll-hint"
        onClick={() => scrollToSection(sobreRef)}
        aria-label="Rolar para a próxima seção"
      >
        <ArrowDown size={20} />
      </button>

    </section>
  );
}

export default Home;